import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { formatDateTime, titleCase } from "../../lib/format";
import type { LedgerAccount } from "../../lib/types";
import { ErrorNotice, PageHeader, Sheet, SheetHeader, Skeleton, Table, Td, Th, Tr } from "../../components/ui";

interface TrialBalance {
  accounts: LedgerAccount[] | null;
  balanced: boolean;
  generated_at: string;
}

const ACCOUNT_DESCRIPTIONS: Record<string, string> = {
  zb_clearing: "Platform funds held at ZB Bank",
  tenant_payable: "Owed to client firms",
  platform_fee_revenue: "Commission PayFlow has earned",
  payout_clearing: "Payouts instructed but not yet settled",
  provider_fees: "ZB Bank's own charges",
};

export function Ledger() {
  const trial = useQuery({
    queryKey: ["trial-balance"],
    queryFn: () => api.get<TrialBalance>("/api/admin/ledger/trial-balance"),
    refetchInterval: 60_000,
  });

  if (trial.isLoading) {
    return (
      <Sheet>
        <Skeleton rows={8} />
      </Sheet>
    );
  }
  if (trial.error) return <ErrorNotice error={trial.error} />;

  const data = trial.data!;
  const accounts = data.accounts ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Platform"
        title="Ledger"
        description="Every account balance, derived from the postings themselves. Balances are never stored, so they cannot drift from what justifies them."
      />

      {/* Whether the ledger balances is the single most important fact on this page,
          so it is stated plainly rather than buried in a column. */}
      <div
        className={`settle settle-1 mb-6 rounded-lg border px-5 py-4 ${
          data.balanced
            ? "border-verdigris/25 bg-verdigris-wash"
            : "border-sienna/30 bg-sienna-wash"
        }`}
      >
        <p
          className={`text-[14px] font-semibold ${
            data.balanced ? "text-verdigris" : "text-sienna"
          }`}
        >
          {data.balanced
            ? "The ledger balances. Debits equal credits in every currency."
            : "The ledger does not balance — investigate immediately."}
        </p>
        <p className="mt-0.5 text-[12px] text-ink-muted">
          Checked {formatDateTime(data.generated_at)}
        </p>
      </div>

      <Sheet className="settle settle-2 overflow-hidden">
        <SheetHeader title="Trial balance" description="Each account in its natural direction" />
        {accounts.length === 0 ? (
          <p className="px-5 py-10 text-center text-[13px] text-ink-muted">
            No postings yet. Balances appear once payments start settling.
          </p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Account</Th>
                <Th>Currency</Th>
                <Th align="right">Balance</Th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <Tr key={`${account.account}-${account.currency}`}>
                  <Td>
                    <span className="font-mono text-[12.5px] text-ink">{account.account}</span>
                    <span className="mt-0.5 block text-[11.5px] text-ink-faint">
                      {ACCOUNT_DESCRIPTIONS[account.account] ?? titleCase(account.account)}
                    </span>
                  </Td>
                  <Td mono>{account.currency}</Td>
                  <Td align="right" mono className="text-[14px] font-medium">
                    {account.formatted}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Sheet>

      <Sheet className="settle settle-3 mt-6 overflow-hidden">
        <SheetHeader title="How the ledger works" />
        <div className="space-y-4 px-5 py-4 text-[13px] leading-relaxed text-ink-muted">
          <p>
            Every movement of money is a balanced set of entries. A successful payment
            debits the clearing account with what actually arrived from the bank, and credits
            the firm's payable and PayFlow's revenue with the split.
          </p>
          <pre className="overflow-x-auto rounded-md bg-paper-sunk px-4 py-3 font-mono text-[11.5px] leading-relaxed">
{`Payment of 100.00, bank keeps 2.50, PayFlow keeps 2.20

  Dr  zb_clearing              97.50
        Cr  tenant_payable     95.30
        Cr  platform_revenue    2.20`}
          </pre>
          <p>
            Entries are never edited. A correction is a reversing posting, so the history of
            what was believed and when stays intact.
          </p>
        </div>
      </Sheet>
    </>
  );
}
